import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CONSUMER_TYPES, PlanSymbol, EendraadSymbol, WALL_FIXTURES, FixtureSymbol } from './symbols';
import { Trash2, RotateCw, Upload, ImageOff, MousePointer2, Minus, Type as TypeIcon, Square } from 'lucide-react';
import { toast } from 'sonner';

const uid = () => Math.random().toString(36).slice(2, 10);
const CANVAS_W = 1200;
const CANVAS_H = 800;
const FOOTER_H = 52;
const PLAN_SYMBOL_VALUES = ['laadpaal', 'omvormer', 'batterij', 'verdeelbord', 'kwh_meter', 'aarding'];

// korte tekstregels, compact bij het symbool
const wrapText = (t, max = 14) => {
  const words = String(t || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if ((cur + ' ' + w).trim().length > max && cur) { lines.push(cur); cur = w; }
    else cur = cur ? `${cur} ${w}` : w;
  }
  if (cur) lines.push(cur);
  return lines;
};

// Situatieplan-editor: muren tekenen, tekstlabels, AREI-symbolen plaatsen op plattegrond
export const SituatiePlan = React.forwardRef(({ situation, onChange, circuits, meta, signature }, ref) => {
  const [tool, setTool] = useState('select'); // select | wall | text | (symbool-type)
  const [selectedId, setSelectedId] = useState(null);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [wallStart, setWallStart] = useState(null);
  const [rectStart, setRectStart] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const dragRef = useRef(null);
  const svgRef = useRef(null);
  const fileInputRef = useRef(null);

  const items = situation?.items || [];
  const walls = situation?.walls || [];
  const setItems = (n) => onChange({ ...situation, items: n });
  const setWalls = (n) => onChange({ ...situation, walls: n });
  const selected = items.find(i => i.id === selectedId);

  // automatisch een aanpasbare rechthoek (woningcontour) bij start
  React.useEffect(() => {
    if (situation && !situation.init_rect && (situation.walls || []).length === 0 && !situation.background) {
      onChange({
        ...situation, init_rect: true, walls: [
          { id: uid(), x1: 300, y1: 200, x2: 900, y2: 200, thickness: 6 },
          { id: uid(), x1: 900, y1: 200, x2: 900, y2: 600, thickness: 6 },
          { id: uid(), x1: 900, y1: 600, x2: 300, y2: 600, thickness: 6 },
          { id: uid(), x1: 300, y1: 600, x2: 300, y2: 200, thickness: 6 },
        ]
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [situation?.init_rect, situation?.background]);

  const svgPoint = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };
  const snap = (v) => Math.round(v / 10) * 10;

  // dichtstbijzijnde punt op een muur (voor raam/deur/garagepoort)
  const nearestOnWall = (p, maxDist = 40) => {
    let best = null;
    for (const w of walls) {
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
      const len2 = dx * dx + dy * dy;
      if (!len2) continue;
      let t = ((p.x - w.x1) * dx + (p.y - w.y1) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const px = w.x1 + t * dx, py = w.y1 + t * dy;
      const dist = Math.hypot(p.x - px, p.y - py);
      if (dist <= maxDist && (!best || dist < best.dist)) {
        best = { x: Math.round(px), y: Math.round(py), rotation: Math.round(Math.atan2(dy, dx) * 180 / Math.PI), dist };
      }
    }
    return best;
  };

  const handleCanvasClick = (e) => {
    // net een fixture geplaatst: de click na mouseup mag de selectie niet wissen
    if (justPlacedRef.current) { justPlacedRef.current = false; return; }
    const p = svgPoint(e);
    if (tool === 'wall') {
      if (!wallStart) {
        setWallStart({ x: snap(p.x), y: snap(p.y) });
      } else {
        // rechte lijnen: snap naar horizontaal/verticaal als bijna recht
        let x2 = snap(p.x), y2 = snap(p.y);
        if (Math.abs(x2 - wallStart.x) < 18) x2 = wallStart.x;
        if (Math.abs(y2 - wallStart.y) < 18) y2 = wallStart.y;
        setWalls([...walls, { id: uid(), x1: wallStart.x, y1: wallStart.y, x2, y2, thickness: 6 }]);
        setWallStart({ x: x2, y: y2 }); // doorlopend tekenen
      }
      return;
    }
    if (tool === 'rect') {
      if (!rectStart) {
        setRectStart({ x: snap(p.x), y: snap(p.y) });
      } else {
        const x2 = snap(p.x), y2 = snap(p.y);
        const { x: x1, y: y1 } = rectStart;
        if (x2 !== x1 && y2 !== y1) {
          setWalls([...walls,
            { id: uid(), x1, y1, x2, y2: y1, thickness: 6 },
            { id: uid(), x1: x2, y1, x2, y2, thickness: 6 },
            { id: uid(), x1: x2, y1: y2, x2: x1, y2, thickness: 6 },
            { id: uid(), x1, y1: y2, x2: x1, y2: y1, thickness: 6 },
          ]);
        }
        setRectStart(null);
      }
      return;
    }
    if (tool === 'text') {
      const label = window.prompt('Tekst (bv. Schuur, Zolder):');
      if (label) {
        const item = { id: uid(), symbol: '_text', x: Math.round(p.x), y: Math.round(p.y), rotation: 0, circuit_no: '', label };
        setItems([...items, item]);
      }
      setTool('select');
      return;
    }
    if (tool !== 'select') {
      if (WALL_FIXTURES.some(f => f.value === tool)) return; // fixtures worden via mousedown+slepen geplaatst
      // symbool plaatsen
      let px = Math.round(p.x), py = Math.round(p.y), rotation = 0;
      const item = { id: uid(), symbol: tool, x: px, y: py, rotation, circuit_no: '', label: '' };
      setItems([...items, item]);
      setSelectedId(item.id);
      if (!e.shiftKey) setTool('select');
      return;
    }
    // select-mode: klik op achtergrond deselecteert
    if (e.target.closest && e.target.closest('[data-testid^="plan-item-"], [data-testid^="plan-wall-"]')) return;
    setSelectedId(null);
    setSelectedWallId(null);
  };

  const fixDragRef = useRef(null);
  const wallDragRef = useRef(null);
  const fixResizeRef = useRef(null);
  const justPlacedRef = useRef(false);
  const handleMouseDown = (e) => {
    if (!WALL_FIXTURES.some(f => f.value === tool)) return;
    const p = svgPoint(e);
    const spot = nearestOnWall(p, 60);
    if (!spot && walls.length) toast.info('Tip: klik dicht bij een muur om het element mee te laten draaien');
    const px = spot ? spot.x : snap(p.x), py = spot ? spot.y : snap(p.y), rotation = spot ? spot.rotation : 0;
    const item = { id: uid(), symbol: tool, x: px, y: py, rotation, circuit_no: '', label: '', width: 24 };
    setItems([...items, item]);
    setSelectedId(item.id);
    fixDragRef.current = { id: item.id, sx: px, sy: py };
  };

  const startDrag = (e, id) => {
    if (tool !== 'select') return;
    e.stopPropagation();
    setSelectedId(id);
    setSelectedWallId(null);
    const p = svgPoint(e);
    const item = items.find(i => i.id === id);
    dragRef.current = { id, dx: p.x - item.x, dy: p.y - item.y };
  };
  const onMove = (e) => {
    if ((tool === 'wall' && wallStart) || (tool === 'rect' && rectStart)) setMousePos(svgPoint(e));
    if (wallDragRef.current) {
      const p = svgPoint(e);
      const { id, end } = wallDragRef.current;
      setWalls(walls.map(w => w.id === id ? (end === 1 ? { ...w, x1: snap(p.x), y1: snap(p.y) } : { ...w, x2: snap(p.x), y2: snap(p.y) }) : w));
      return;
    }
    if (fixResizeRef.current) {
      // schalen vanaf de rand: het andere uiteinde blijft vast
      const p = svgPoint(e);
      const { id, fx, fy, dir } = fixResizeRef.current;
      const it = items.find(i => i.id === id);
      if (it) {
        const rad = (it.rotation || 0) * Math.PI / 180;
        const ux = Math.cos(rad), uy = Math.sin(rad);
        const t = Math.max(20, dir * ((p.x - fx) * ux + (p.y - fy) * uy));
        setItems(items.map(i => i.id === id ? { ...i, width: Math.round(t), x: Math.round(fx + dir * (t / 2) * ux), y: Math.round(fy + dir * (t / 2) * uy) } : i));
      }
      return;
    }
    if (fixDragRef.current) {
      // initieel plaatsen: slepen vanaf het startpunt (rand), niet vanuit het midden
      const p = svgPoint(e);
      const { id, sx, sy } = fixDragRef.current;
      const spot = nearestOnWall(p, 60);
      const ex = spot ? spot.x : snap(p.x), ey = spot ? spot.y : snap(p.y);
      const w = Math.max(24, Math.round(Math.hypot(ex - sx, ey - sy)));
      const rotation = spot ? spot.rotation : Math.round(Math.atan2(ey - sy, ex - sx) * 180 / Math.PI);
      setItems(items.map(i => i.id === id ? { ...i, width: w, rotation, x: Math.round((sx + ex) / 2), y: Math.round((sy + ey) / 2) } : i));
      return;
    }
    if (!dragRef.current) return;
    const p = svgPoint(e);
    const { id, dx, dy } = dragRef.current;
    const dragged = items.find(i => i.id === id);
    // fixtures (raam/deur/poort) blijven op de muur snappen tijdens slepen
    if (dragged && WALL_FIXTURES.some(f => f.value === dragged.symbol)) {
      const spot = nearestOnWall({ x: p.x, y: p.y }, 60);
      if (spot) {
        setItems(items.map(i => i.id === id ? { ...i, x: spot.x, y: spot.y, rotation: spot.rotation } : i));
      }
      return;
    }
    setItems(items.map(i => i.id === id ? { ...i, x: Math.round(p.x - dx), y: Math.round(p.y - dy) } : i));
  };
  const endDrag = () => {
    dragRef.current = null;
    wallDragRef.current = null;
    fixResizeRef.current = null;
    if (fixDragRef.current) {
      setSelectedId(fixDragRef.current.id);
      fixDragRef.current = null;
      justPlacedRef.current = true;
      setTool('select');
    }
  };

  const patchSelected = (patch) => setItems(items.map(i => i.id === selectedId ? { ...i, ...patch } : i));
  const deleteSelected = () => { setItems(items.filter(i => i.id !== selectedId)); setSelectedId(null); };
  const deleteSelectedWall = () => { setWalls(walls.filter(w => w.id !== selectedWallId)); setSelectedWallId(null); };

  const handleUploadPlan = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1600 / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange({ ...situation, background: canvas.toDataURL('image/jpeg', 0.82) });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const toolBtn = (value, icon, label) => (
    <button type="button" onClick={() => { setTool(value); setWallStart(null); setRectStart(null); }}
      className={`flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 text-xs transition-colors ${tool === value ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'border-border/50 hover:border-orange-400/50'}`}
      data-testid={`plan-tool-${value}`}>
      {icon}{label}
    </button>
  );

  return (
    <div className="flex gap-4 h-full" data-testid="situatieplan-editor">
      {/* Palet */}
      <div className="w-60 flex-shrink-0 border border-border/60 rounded-lg p-3 overflow-y-auto max-h-[75vh]">
        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Tekengereedschap</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {toolBtn('select', <MousePointer2 className="w-3.5 h-3.5" />, 'Selecteer')}
          {toolBtn('wall', <Minus className="w-3.5 h-3.5" />, 'Muur / lijn')}
          {toolBtn('rect', <Square className="w-3.5 h-3.5" />, 'Rechthoek')}
          {toolBtn('text', <TypeIcon className="w-3.5 h-3.5" />, 'Tekst')}
        </div>
        {tool === 'rect' && (
          <p className="text-[10px] text-muted-foreground mb-2">Klik twee hoekpunten om een rechthoek van muren te tekenen.</p>
        )}
        {tool === 'wall' && (
          <p className="text-[10px] text-muted-foreground mb-2">Klik om muren te tekenen (doorlopend). Kies "Selecteer" om te stoppen.</p>
        )}
        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Op muur (raam / deur / poort)</p>
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {WALL_FIXTURES.map(f => (
            <button key={f.value} type="button"
              onClick={() => { setTool(tool === f.value ? 'select' : f.value); setWallStart(null); }}
              className={`flex flex-col items-center gap-1 border rounded-md p-2 text-[10px] transition-colors ${tool === f.value ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'border-border/50 hover:border-orange-400/50'}`}
              data-testid={`plan-fixture-${f.value}`}>
              <svg width={40} height={26} viewBox="-24 -14 48 28"><FixtureSymbol type={f.value} size={f.value === 'garagepoort' ? 40 : 30} /></svg>
              <span>{f.label}</span>
            </button>
          ))}
        </div>
        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Symbolen</p>
        <div className="grid grid-cols-2 gap-1.5">
          {CONSUMER_TYPES.filter(t => PLAN_SYMBOL_VALUES.includes(t.value)).map(t => (
            <button key={t.value} type="button"
              onClick={() => { setTool(tool === t.value ? 'select' : t.value); setWallStart(null); }}
              className={`flex flex-col items-center gap-1 border rounded-md p-2 text-[10px] transition-colors ${tool === t.value ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'border-border/50 hover:border-orange-400/50'}`}
              data-testid={`plan-symbol-${t.value}`}>
              <svg width={30} height={30} viewBox="-16 -20 32 36"><EendraadSymbol type={t.value === 'batterij' ? 'thuisbatterij' : t.value} size={16} /></svg>
              <span className="leading-tight text-center">{t.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadPlan} />
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => fileInputRef.current?.click()} data-testid="upload-plan-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Plattegrond uploaden
          </Button>
          {situation?.background && (
            <Button variant="outline" size="sm" className="w-full text-xs text-destructive" onClick={() => onChange({ ...situation, background: null })}>
              <ImageOff className="w-3.5 h-3.5 mr-1" /> Plattegrond verwijderen
            </Button>
          )}
        </div>
        {selected && (
          <div className="mt-4 border-t border-border/50 pt-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase">Geselecteerd</p>
            <p className="text-xs">{selected.symbol === '_text' ? 'Tekstlabel' : (WALL_FIXTURES.find(f => f.value === selected.symbol)?.label || CONSUMER_TYPES.find(c => c.value === selected.symbol)?.label)}</p>
            {selected.symbol !== '_text' && !WALL_FIXTURES.some(f => f.value === selected.symbol) && (
              <select value={selected.circuit_no} onChange={e => patchSelected({ circuit_no: e.target.value })}
                className="w-full h-8 text-xs rounded-md border border-input bg-background px-2" data-testid="selected-circuit-select">
                <option value="">Geen kring</option>
                {(circuits || []).map(c => <option key={c.circuit_no} value={c.circuit_no}>{`Kring ${c.circuit_no} \u2014 ${c.label || ''}`}</option>)}
              </select>
            )}
            <Input className="h-8 text-xs" placeholder="Label / tekst" value={selected.label || ''} onChange={e => patchSelected({ label: e.target.value })} />
            {WALL_FIXTURES.some(f => f.value === selected.symbol) ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Breedte</span>
                <Input type="number" min={20} max={400} className="h-8 text-xs w-20"
                  value={selected.width || (selected.symbol === 'garagepoort' ? 160 : 44)}
                  onChange={e => patchSelected({ width: Math.max(20, parseInt(e.target.value) || 44) })}
                  data-testid="fixture-width-input" />
                <span className="text-xs text-muted-foreground">px</span>
              </div>
            ) : selected.symbol !== '_text' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Grootte</span>
                <Input type="number" min={14} max={200} className="h-8 text-xs w-20"
                  value={selected.size || 30}
                  onChange={e => patchSelected({ size: Math.max(14, parseInt(e.target.value) || 30) })}
                  data-testid="symbol-size-input" />
                <span className="text-xs text-muted-foreground">px</span>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => patchSelected({ rotation: ((selected.rotation || 0) + 90) % 360 })} data-testid="rotate-plan-item-btn">
                <RotateCw className="w-3.5 h-3.5 mr-1" /> Draai
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs text-destructive" onClick={deleteSelected} data-testid="delete-plan-item-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Weg
              </Button>
            </div>
          </div>
        )}
        {selectedWallId && (
          <div className="mt-4 border-t border-border/50 pt-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase">Muur geselecteerd</p>
            <Button variant="outline" size="sm" className="w-full text-xs text-destructive" onClick={deleteSelectedWall} data-testid="delete-wall-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Muur verwijderen
            </Button>
          </div>
        )}
      </div>

      {/* Canvas */}
      <div className="flex-1 border border-border/60 rounded-lg overflow-auto bg-white" style={{ cursor: tool === 'select' ? 'default' : 'crosshair' }}>
        <svg ref={(el) => { svgRef.current = el; if (ref) ref.current = el; }}
          viewBox={`0 0 ${CANVAS_W} ${CANVAS_H + FOOTER_H}`} width="100%" style={{ color: '#1a1a1a', minHeight: '60vh' }}
          xmlns="http://www.w3.org/2000/svg"
          onClick={handleCanvasClick} onMouseDown={handleMouseDown} onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}
          data-testid="situatieplan-canvas">
          {situation?.background ? (
            <image href={situation.background} x={0} y={0} width={CANVAS_W} height={CANVAS_H} preserveAspectRatio="xMidYMid meet" />
          ) : (
            <>
              <rect width={CANVAS_W} height={CANVAS_H} fill="#ffffff" />
              {Array.from({ length: Math.floor(CANVAS_W / 40) }).map((_, i) => (
                <line key={`v${i}`} x1={i * 40} y1={0} x2={i * 40} y2={CANVAS_H} stroke="#f2f2f2" strokeWidth={1} />
              ))}
              {Array.from({ length: Math.floor(CANVAS_H / 40) }).map((_, i) => (
                <line key={`h${i}`} x1={0} y1={i * 40} x2={CANVAS_W} y2={i * 40} stroke="#f2f2f2" strokeWidth={1} />
              ))}
            </>
          )}
          {/* Muren */}
          {walls.map(w => (
            <line key={w.id} x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2}
              stroke={w.id === selectedWallId ? '#e67e22' : '#2b2b2b'} strokeWidth={w.thickness || 6} strokeLinecap="square"
              style={{ cursor: 'pointer' }}
              onClick={(e) => { if (tool === 'select') { e.stopPropagation(); if (justPlacedRef.current) { justPlacedRef.current = false; return; } setSelectedWallId(w.id); setSelectedId(null); } }}
              data-testid={`plan-wall-${w.id}`} />
          ))}
          {/* Preview lijn tijdens tekenen */}
          {tool === 'wall' && wallStart && mousePos && (
            <line x1={wallStart.x} y1={wallStart.y} x2={snap(mousePos.x)} y2={snap(mousePos.y)}
              stroke="#e67e22" strokeWidth={4} strokeDasharray="8 4" opacity={0.7} />
          )}
          {/* Preview rechthoek */}
          {tool === 'rect' && rectStart && mousePos && (
            <rect x={Math.min(rectStart.x, snap(mousePos.x))} y={Math.min(rectStart.y, snap(mousePos.y))}
              width={Math.abs(snap(mousePos.x) - rectStart.x)} height={Math.abs(snap(mousePos.y) - rectStart.y)}
              fill="none" stroke="#e67e22" strokeWidth={4} strokeDasharray="8 4" opacity={0.7} />
          )}
          {/* Items */}
          {items.map(item => (
            <g key={item.id} transform={`translate(${item.x}, ${item.y}) rotate(${item.rotation || 0})`}
              onMouseDown={(e) => startDrag(e, item.id)} style={{ cursor: tool === 'select' ? 'move' : 'inherit' }}
              data-testid={`plan-item-${item.id}`}>
              {item.id === selectedId && <circle r={24} fill="rgba(230,126,34,0.12)" stroke="#e67e22" strokeWidth={1} strokeDasharray="4 3" />}
              {item.symbol === '_text' ? (
                <text fontSize={16} fontWeight="600" fill="#333" textAnchor="middle">{item.label}</text>
              ) : WALL_FIXTURES.some(f => f.value === item.symbol) ? (
                <>
                  <FixtureSymbol type={item.symbol} size={item.width || (item.symbol === 'garagepoort' ? 160 : 44)} />
                  {item.label && (
                    <g transform={`rotate(${-(item.rotation || 0)})`}>
                      {wrapText(item.label).map((l, i) => (
                        <text key={i} y={18 + i * 10} textAnchor="middle" fontSize={9} fill="#333">{l}</text>
                      ))}
                    </g>
                  )}
                </>
              ) : (
                <>
                  <PlanSymbol type={item.symbol} size={item.size || 30} circuitNo={item.circuit_no} />
                  <g transform={`rotate(${-(item.rotation || 0)})`}>
                    {(() => {
                      const sz = item.size || 30;
                      const lines = [];
                      if (item.label) lines.push(...wrapText(item.label));
                      if (['omvormer', 'batterij'].includes(item.symbol)) {
                        const info = (circuits || []).flatMap(c2 => c2.consumers || []).find(co => co.type === item.symbol && (co.brand || co.sn));
                        if (info) {
                          if (info.brand) lines.push(...wrapText(`${info.brand}${info.model ? ` ${info.model}` : ''}`));
                          if (info.sn) lines.push(`SN: ${info.sn}`);
                        }
                      }
                      return lines.map((l, i) => (
                        <text key={i} y={sz / 2 + 10 + i * 9} textAnchor="middle" fontSize={8.5} fill="#333">{l}</text>
                      ));
                    })()}
                  </g>
                </>
              )}
            </g>
          ))}
          {/* Handvatten muur: uiteinden verslepen */}
          {tool === 'select' && selectedWallId && (() => {
            const w = walls.find(x => x.id === selectedWallId);
            if (!w) return null;
            return [1, 2].map(end => (
              <circle key={end} cx={end === 1 ? w.x1 : w.x2} cy={end === 1 ? w.y1 : w.y2} r={8}
                fill="#ffffff" stroke="#e67e22" strokeWidth={2} style={{ cursor: 'grab' }}
                onMouseDown={(e) => { e.stopPropagation(); wallDragRef.current = { id: w.id, end }; }}
                data-testid={`wall-handle-${end}`} />
            ));
          })()}
          {/* Handvatten raam/deur/poort: schalen vanaf de rand */}
          {tool === 'select' && selected && WALL_FIXTURES.some(f => f.value === selected.symbol) && (() => {
            const wdt = selected.width || (selected.symbol === 'garagepoort' ? 160 : 44);
            const rad = (selected.rotation || 0) * Math.PI / 180;
            const ux = Math.cos(rad), uy = Math.sin(rad);
            return [-1, 1].map(dir => (
              <circle key={dir} cx={selected.x + dir * (wdt / 2) * ux} cy={selected.y + dir * (wdt / 2) * uy} r={7}
                fill="#ffffff" stroke="#e67e22" strokeWidth={2} style={{ cursor: 'ew-resize' }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  fixResizeRef.current = { id: selected.id, fx: selected.x - dir * (wdt / 2) * ux, fy: selected.y - dir * (wdt / 2) * uy, dir };
                }}
                data-testid={`fixture-handle-${dir === 1 ? 'end' : 'start'}`} />
            ));
          })()}
          {/* Handtekening elektricien (rechtsonder, boven het onderschrift) */}
          {signature?.image && (
            <g data-testid="situatie-signature">
              <rect x={CANVAS_W - 252} y={CANVAS_H - 110} width={244} height={98} fill="#fff" stroke="#555" strokeWidth={0.9} rx={3} />
              <text x={CANVAS_W - 245} y={CANVAS_H - 96} fontSize={9} fontWeight="bold" fill="#111">Handtekening installateur</text>
              <image href={signature.image} x={CANVAS_W - 245} y={CANVAS_H - 91} width={138} height={60} preserveAspectRatio="xMinYMid meet" />
              <text x={CANVAS_W - 245} y={CANVAS_H - 22} fontSize={9.5} fill="#111">{signature.name || ''}</text>
              <text x={CANVAS_W - 95} y={CANVAS_H - 22} fontSize={9.5} fill="#333" textAnchor="start">{signature.date ? new Date(signature.date).toLocaleDateString('nl-BE') : ''}</text>
            </g>
          )}
          {/* Vast onderschrift: locatie, installateur, type net */}
          <g data-testid="situatieplan-footer">
            <rect x={0} y={CANVAS_H} width={CANVAS_W} height={FOOTER_H} fill="#ffffff" />
            <line x1={10} y1={CANVAS_H + 4} x2={CANVAS_W - 10} y2={CANVAS_H + 4} stroke="#999" strokeWidth={0.8} />
            <text x={14} y={CANVAS_H + 18} fontSize={11} fontWeight="bold" fill="#111">
              {`Locatie elektrische installatie: ${meta?.customerName || '-'}${meta?.address ? ` \u2014 ${meta.address}` : ''}`}
            </text>
            <text x={14} y={CANVAS_H + 32} fontSize={10} fill="#333">
              {`Installateur: ${meta?.company?.name || ''}${meta?.company?.address ? ` \u00b7 ${meta.company.address}` : ''}${meta?.company?.phone ? ` \u00b7 ${meta.company.phone}` : ''}${meta?.company?.email ? ` \u00b7 ${meta.company.email}` : ''}${meta?.company?.vat ? ` \u00b7 BTW ${meta.company.vat}` : ''}`}
            </text>
            <text x={14} y={CANVAS_H + 46} fontSize={10} fill="#333">
              {`Type net: ${meta?.netType || '230V'} ~ 50Hz \u00b7 Datum: ${new Date().toLocaleDateString('nl-BE')}`}
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
});

export default SituatiePlan;

